/* eslint-disable react-refresh/only-export-components */

import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
	createContext,
	type Dispatch,
	type FC,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { toast } from "react-toastify";
import type { WithChildren } from "../../../../_metronic/helpers";
import { LayoutSplashScreen } from "../../../../_metronic/layout/core";
import { decryptId } from "../../../modules/auth/core/crypto";
import { refreshToken } from "../../../pages/users/api/user.api";
import { navigate } from "../../../routing/navigationService";
import type { AuthModel, UserModel } from "./_models";
import { getUserByToken } from "./_requests";
import * as authHelper from "./AuthHelpers";

type AuthContextProps = {
	auth: AuthModel | undefined;
	saveAuth: (auth: AuthModel | undefined) => void;
	currentUser: UserModel | undefined;
	setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
	logout: () => void;
	isAuthReady: boolean;
	setIsAuthReady: Dispatch<SetStateAction<boolean>>;
};

const initAuthContextPropsState: AuthContextProps = {
	auth: authHelper.getAuth(),
	saveAuth: () => {},
	currentUser: undefined,
	setCurrentUser: () => {},
	logout: () => {},
	isAuthReady: false,
	setIsAuthReady: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => useContext(AuthContext);
// const navigate=useNavigate()
// Helper to decode JWT and check expiry
const isTokenExpired = (token: string): boolean => {
	if (!token || !token.includes(".")) return false; // Not a JWT, likely mock/test token
	try {
		const payloadBase64 = token.split(".")[1];
		if (!payloadBase64) return false;
		const decoded = JSON.parse(atob(payloadBase64));
		const exp = decoded.exp;

		if (!exp) return false; // No expiry, assume valid

		const now = Math.floor(Date.now() / 1000);
		const expired = exp < now;

		if (expired) {
			toast.info("Session expired. Please log in again.");
		}

		return expired;
	} catch (e) {
		console.error("Token decode failed", e);
		return false; // Don't block on decode error during development
	}
};
const AuthProvider: FC<WithChildren> = ({ children }) => {
	const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
	const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
	// Initialize as ready if token exists
	const [isAuthReady, setIsAuthReady] = useState(
		!!authHelper.getAuth()?.access_token,
	);
	const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const saveAuth = useCallback((auth: AuthModel | undefined) => {
		setAuth(auth);
		if (auth) {
			authHelper.setAuth(auth);
		} else {
			authHelper.removeAuth();
			if (refreshTimer.current) clearTimeout(refreshTimer.current);
		}
	}, []);

	const logOutFunction = useCallback(async () => {
		const currentAuth = authHelper.getAuth();
		try {
			// Only hit the logout API if we actually have a session token
			if (currentAuth?.access_token) {
				await axios.get("/admin/user/logout");
			}
		} catch (error) {
			console.error("Logout failed", error);
		} finally {
			// Always cleanup local state even if backend call fails
			setCurrentUser(undefined);
			saveAuth(undefined);
			sessionStorage.clear();
			localStorage.clear();

			// Only navigate if we aren't already on an auth page
			if (!window.location.pathname.startsWith("/auth")) {
				navigate("/auth");
			}
		}
	}, [saveAuth]);

	const logout = useCallback(async () => {
		await logOutFunction();
	}, [logOutFunction]);

	const scheduleRefresh = useCallback(
		(accessToken: string) => {
			if (refreshTimer.current) clearTimeout(refreshTimer.current);

			try {
				const { exp } = jwtDecode<any>(accessToken);
				const now = Date.now();
				const expiryTimeMs = exp * 1000;
				const expiresInMs = expiryTimeMs - now;

				const totalSeconds = Math.floor(expiresInMs / 1000);
				const minutes = Math.floor(totalSeconds / 60);
				const seconds = totalSeconds % 60;

				const refreshTime = expiresInMs - 30 * 1000;
				const refreshSeconds = Math.floor(refreshTime / 1000);
				const refreshMinutes = Math.floor(refreshSeconds / 60);
				const refreshRemainSeconds = refreshSeconds % 60;

				console.log(
					"%c TOKEN STATUS",
					"color:#00e5ff; font-weight:bold; font-size:14px;",
				);

				console.log(
					`%cAccess Token Expires In: %c${minutes}m ${seconds}s`,
					"color:#fff; font-size:13px;",
					"color:#4CAF50; font-weight:bold;",
				);

				console.log(
					`%cRefresh Will Trigger In: %c${refreshMinutes}m ${refreshRemainSeconds}s`,
					"color:#fff; font-size:13px;",
					"color:#FFC107; font-weight:bold;",
				);

				console.log(
					`%cExact Expiry Time: %c${new Date(expiryTimeMs).toLocaleString()}`,
					"color:#fff; font-size:13px;",
					"color:#90CAF9; font-weight:bold;",
				);

				console.log(
					"%c--------------------------------------------",
					"color:#555;",
				);

				// console.log("refresh token", expiresInMs);

				if (refreshTime > 0) {
					refreshTimer.current = setTimeout(async () => {
						try {
							const { data } = await refreshToken();
							if (data?.accessToken) {
								const updatedAuth = {
									...(auth || {}),
									access_token: data.accessToken,
								} as AuthModel;
								saveAuth(updatedAuth);
							}
						} catch (err) {
							console.error("Token refresh failed", err);
							logout();
						}
					}, refreshTime);
				}
			} catch (err) {
				console.error("Invalid JWT token", err);
				logout();
			}
		},
		[auth, logout, saveAuth], // depends on current auth
	);

	useEffect(() => {
		if (auth?.access_token) {
			scheduleRefresh(auth.access_token);
		}
		return () => {
			if (refreshTimer.current) clearTimeout(refreshTimer.current);
		};
	}, [auth?.access_token, scheduleRefresh]); // run only once on mount

	return (
		<AuthContext.Provider
			value={{
				auth,
				saveAuth,
				currentUser,
				setCurrentUser,
				logout,
				isAuthReady,
				setIsAuthReady,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

const AuthInit: FC<WithChildren> = ({ children }) => {
	const queryClient = useQueryClient();
	const {
		auth,
		currentUser,
		logout,
		setCurrentUser,
		isAuthReady,
		setIsAuthReady,
	} = useAuth();
	const [showSplashScreen, setShowSplashScreen] = useState(true);

	useEffect(() => {
		const init = async () => {
			if (!auth || !auth.access_token || isTokenExpired(auth.access_token)) {
				logout();
				queryClient.clear();
				setShowSplashScreen(false);
				return;
			}

			try {
				// Decode JWT to extract user data
				let jwtData: any = {};
				try {
					const decoded = jwtDecode<any>(auth.access_token);
					jwtData = {
						user_id: decoded.user_id,
						state_id: decoded.state_id,
						district_id: decoded.district_id,
						block_id: decoded.block_id,
						employee_code: decoded.employee_code,
						tenant_id: decoded.tenant_id,
						email: decoded.email,
						name: decoded.name,
						mobile_no: decoded.mobile_no,
						profile_status: decoded.profile_status,
						profile_step: decoded.profile_step,
					};
				} catch (e) {
					console.error("Failed to decode JWT token:", e);
				}

				if (!currentUser) {
					// First time - fetch user data from API
					const res: any = await getUserByToken(jwtData.user_id);
					const data = res?.data?.data;

					// Merge JWT data with API data (API takes precedence for user details like name)
					// Only use JWT values when they are defined
					const mergedUser = {
						...jwtData,
						...data,
						// Ensure JWT values are used for these critical fields if API doesn't have them
						user_id: data?.user_id || jwtData?.user_id,
						state_id: data?.state_id || jwtData?.state_id,
						district_id: data?.district_id || jwtData?.district_id,
						block_id: data?.block_id || jwtData?.block_id,
						employee_code: data?.employee_code || jwtData?.employee_code,
					};

					if (mergedUser.role_id) {
						if (typeof mergedUser.role_id === "string") {
							try {
								const decryptedRoleId = await decryptId(mergedUser.role_id);
								mergedUser.role_id = Number(decryptedRoleId);
							} catch (e) {
								console.error("Failed to decrypt role_id", e);
							}
						} else {
							mergedUser.role_id = Number(mergedUser.role_id);
						}
					}

					setCurrentUser(mergedUser);
					setIsAuthReady(true);
				} else {
					// User exists - ensure IDs are populated from JWT if missing
					if (!currentUser.district_id && jwtData.district_id) {
						const updatedUser = {
							...currentUser,
							...jwtData,
						};
						setCurrentUser(updatedUser);
					}
					setIsAuthReady(true);
				}

				setShowSplashScreen(false);
			} catch (err) {
				console.error("Failed to fetch user by token:", err);
				setShowSplashScreen(false);
				logout();
			}
		};

		init();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		auth,
		currentUser,
		logout,
		queryClient.clear,
		setCurrentUser,
		setIsAuthReady,
	]);

	return showSplashScreen || (auth && !currentUser) ? (
		<LayoutSplashScreen />
	) : (
		children
	);
};

export { AuthInit, AuthProvider, useAuth };
